/**
 * ConfirmDialog — modal confirmation for destructive actions.
 *
 * Built on the native `<dialog>` element so we get the escape-to-close,
 * focus-trap, and aria-modal semantics for free without pulling in a
 * focus-trap library. Used by every admin page that fires a delete /
 * archive / deactivate write — the cost of pausing one extra second is
 * vastly cheaper than recovering from an accidental click.
 */

import { useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

interface Props {
  open: boolean;
  title: string;
  body?: string;
  confirmLabel?: string;
  /** When true, paint the confirm button in the rejection palette so
   *  irreversible operations look distinct from neutral confirmations. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

export function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  destructive,
  onConfirm,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    if (open && !el.open) el.showModal();
    if (!open && el.open) el.close();
  }, [open]);

  const confirmClasses = destructive
    ? "bg-rejection text-white dark:bg-rejection-dark hover:opacity-90"
    : "bg-fg dark:bg-fg-dark text-canvas dark:text-canvas-dark hover:opacity-90";

  return (
    <dialog
      ref={ref}
      onCancel={(e) => {
        e.preventDefault();
        onCancel();
      }}
      className="rounded-xl border border-hairline dark:border-hairline-dark bg-surface dark:bg-surface-dark p-0 backdrop:bg-fg/40 dark:backdrop:bg-canvas-dark/70"
    >
      <div className="w-[min(28rem,90vw)] p-6">
        <h3 className="rtl-isolate text-lg font-bold text-fg dark:text-fg-dark mb-2">{title}</h3>
        {body ? (
          <p className="rtl-isolate text-sm text-muted dark:text-muted-dark mb-4">{body}</p>
        ) : null}
        <div className="flex items-center justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-md px-3 py-1.5 text-sm text-fg dark:text-fg-dark hover:bg-hairline/40 dark:hover:bg-hairline-dark/40"
          >
            {t("common.cancel")}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`rounded-md px-3 py-1.5 text-sm font-semibold ${confirmClasses}`}
          >
            {confirmLabel ?? t("common.confirm")}
          </button>
        </div>
      </div>
    </dialog>
  );
}
